/**
 * KIOSK API client。
 *
 * 自助購票機為公開機台 —— products / schedules / recommendations / orders /
 * payment-callback 端點皆不需認證（後端無 guard）。唯一需認證的是票券 QR
 * （GET /tickets/:id/qr，audit_log 寫 ticket.qr_view）；故 KIOSK 以服務帳號
 * 在背景登入一次，僅供出票後拉 qr_payload 列印用，對顧客全程透明。
 */

const API_BASE: string = import.meta.env.VITE_API_BASE ?? 'http://localhost:3000/api/v1';
const KIOSK_EMAIL: string = import.meta.env.VITE_KIOSK_EMAIL ?? 'admin@boat-ticket.dev';
const KIOSK_PASSWORD: string = import.meta.env.VITE_KIOSK_PASSWORD ?? 'Dev-Pass-2026!';

/** 心跳回報間隔（毫秒）；與後端 sweeper 離線閾值搭配（後端 30s / 離線 90s） */
export const KIOSK_HEARTBEAT_INTERVAL_MS = 30_000;

// ---------- 型別（與後端 DTO 對齊；子集自 apps/pwa api-client）----------

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  [key: string]: unknown;
}

export interface ProductSummary {
  id: string;
  sku: string;
  category: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string> | null;
  /** numeric string from PostgreSQL */
  base_price: string;
  currency: string;
  duration_minutes: number | null;
  channels: string[];
  is_published: boolean;
}
export interface ProductListResponse {
  items: ProductSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ScheduleSummary {
  id: string;
  product_id: string;
  departure_at: string;
  departure_end_at: string;
  capacity: number;
  reserved_count: number;
  weather_cancelled: boolean;
}
export interface ScheduleListResponse {
  items: ScheduleSummary[];
}

export type KioskTicketType =
  | 'adult'
  | 'senior'
  | 'child'
  | 'infant'
  | 'early_bird'
  | 'package_family'
  | 'custom';

export interface CreateOrderItem {
  product_id: string;
  schedule_id: string;
  quantity: number;
  unit_price: number | string;
  /** 票種；後端依此解析對應 pricing 規則（成人/兒童）。 */
  ticket_type?: KioskTicketType;
  /**
   * 這筆是哪張套票的一段；單買時省略。
   *
   * 套票不建班次——顧客為每一段各挑一個時段，同一張套票的每一段都帶同一個值。
   * 後端據此驗證組合完整、時段不重疊，並把打包價分攤到各段。
   */
  package_product_id?: string;
}
export interface CreateOrderRequest {
  channel: 'web' | 'kiosk' | 'partner';
  subtotal: number | string;
  total: number | string;
  items: CreateOrderItem[];
  buyer_email?: string;
}
export type OrderState =
  | 'HOLD'
  | 'PAYMENT_PENDING'
  | 'PAID'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'PAYMENT_FAILED'
  | 'OVERSOLD';
export interface OrderSummary {
  id: string;
  state: OrderState;
  subtotal: string;
  total: string;
  hold_expires_at?: string;
}
export interface PaymentCallbackResponse {
  outcome: 'paid' | 'oversold' | 'failed';
  order: OrderSummary;
  issuedTicketIds?: string[];
}

export interface RecommendRequest {
  stay_minutes: number;
  budget?: number | null;
  party_adults: number;
  party_children?: number;
  interests?: string[];
  /**
   * 出發日期 `YYYY-MM-DD`（Asia/Taipei）。
   *
   * 給了就只推薦當天真的有位子的商品（未停航、剩餘座位夠這一團）；
   * 不給則不看班次，維持語音入口等既有呼叫端的行為。
   */
  date?: string;
}
export interface RecommendProduct {
  product_id: string;
  sku: string;
  name_i18n: Record<string, string>;
  category: string;
  duration_minutes: number;
  base_price: string;
}
export interface RecommendItinerary {
  id: string;
  products: RecommendProduct[];
  estimated_minutes: number;
  total_price: string;
  fit_note: string;
}
export interface RecommendResponse {
  party_size: number;
  stay_minutes: number;
  budget: number | null;
  recommendations: RecommendItinerary[];
}

export type TicketState = 'valid' | 'used' | 'void_pending_refund' | 'voided' | 'expired';
export interface TicketQr {
  ticket_id: string;
  ticket_code: string;
  ticket_state: TicketState;
  refund_risk_class: 'REFUND_ELIGIBLE' | 'REFUND_LOCKED';
  /** compact JWS — 直接給 QR encoder */
  qr_payload: string;
}

// ---------- fetch wrapper ----------

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly problem?: ProblemDetails;
  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(message);
    this.status = status;
    if (problem !== undefined) this.problem = problem;
  }
}

interface FetchInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
  token?: string;
}

async function apiFetch<T = unknown>(path: string, init: FetchInit = {}): Promise<T> {
  const { body, token, headers: initHeaders, ...rest } = init;
  const headers = new Headers(initHeaders);
  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const requestInit: RequestInit = { ...rest, headers, credentials: 'include' };
  if (body !== undefined) {
    requestInit.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, requestInit);
  if (!res.ok) {
    let problem: ProblemDetails | undefined;
    if ((res.headers.get('content-type') ?? '').includes('json')) {
      try {
        problem = (await res.json()) as ProblemDetails;
      } catch {
        /* non-json error body */
      }
    }
    throw new ApiError(
      res.status,
      problem?.detail ?? problem?.title ?? `HTTP ${res.status}`,
      problem,
    );
  }
  if (res.status === 204) return undefined as T;
  if (!(res.headers.get('content-type') ?? '').includes('json')) return undefined as T;
  return (await res.json()) as T;
}

/** Idempotency-Key（POST /orders 必帶）。 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `kiosk-${crypto.randomUUID()}`;
  }
  return `kiosk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------- 公開端點（不需認證）----------

export interface KioskHeartbeatResponse {
  device_id: string;
  device_code: string;
  online: boolean;
  last_heartbeat: string;
}

/**
 * KIOSK 上線回報（POST /kiosk/heartbeat）。
 *
 * 公開端點；後端 upsert device_status(online=true, last_heartbeat=NOW())，
 * 供 CMS 設備儀表板（AC-H-3）顯示在線。device_code 未登錄 → 404（ApiError）。
 *
 * device_code 由呼叫端（useKioskHeartbeat）以 runtime 綁定值傳入；
 * 機台身分管理見 lib/device-identity。
 */
export function reportKioskHeartbeat(
  deviceCode: string,
  appVersion?: string,
): Promise<KioskHeartbeatResponse> {
  return apiFetch<KioskHeartbeatResponse>('/kiosk/heartbeat', {
    method: 'POST',
    body: appVersion
      ? { device_code: deviceCode, app_version: appVersion }
      : { device_code: deviceCode },
  });
}

/** 機台綁定前驗證 device_code（GET /kiosk/device-check）。公開端點。 */
export interface KioskDeviceCheck {
  device_code: string;
  exists: boolean;
  bindable: boolean;
  retired: boolean;
  device_type: string | null;
}
export function checkDevice(deviceCode: string): Promise<KioskDeviceCheck> {
  return apiFetch<KioskDeviceCheck>(
    `/kiosk/device-check?device_code=${encodeURIComponent(deviceCode)}`,
    { method: 'GET' },
  );
}

/**
 * 可售商品清單。
 *
 * `category` 對應 products.category（trip 航程／experience 水上體驗／
 * package 套裝／charter 包船）；不給則不過濾。過濾在後端做，機台不必把
 * 全部商品抓下來再自己篩。
 */
export function listProducts(category?: string): Promise<ProductListResponse> {
  const q = new URLSearchParams({
    // CMS 的「銷售通路」設定要真的管得到機台：沒帶這個參數的話，
    // 標成僅限官網的商品照樣會出現在購票機上，那個欄位就形同虛設
    channel: 'kiosk',
    // 未上架的商品不該離開後端。原本是抓回全部再由畫面自己濾掉，
    // 等於把還沒公開的定價與名稱送到公開機台上
    is_published: 'true',
  });
  if (category !== undefined && category !== '') q.set('category', category);
  return apiFetch<ProductListResponse>(`/products?${q.toString()}`, { method: 'GET' });
}

/**
 * 某商品可售的班次（依出發時間遞增）。
 *
 * 只取此刻之後的：已開航的班次不該出現在購票機上。預設 limit 是 20，對「一天
 * 五班、排兩週」來說只夠看四天，故放寬到 60（約兩週的量）。
 */
export function listSchedules(productId: string): Promise<ScheduleListResponse> {
  const qs = new URLSearchParams({
    product_id: productId,
    departure_after: new Date().toISOString(),
    limit: '60',
  });
  return apiFetch<ScheduleListResponse>(`/schedules?${qs.toString()}`, { method: 'GET' });
}

/** 套票的一段（GET /products/:id/components）。 */
export interface PackageComponent {
  component_product_id: string;
  sort_order: number;
  sku: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string> | null;
  duration_minutes: number | null;
  base_price: string;
  cover_media_id: string | null;
}

/**
 * 套票含哪些商品（依呈現順序）。
 *
 * 套票商品不建班次，機台拿到組成後再逐段查各自的班次讓顧客挑。回空陣列代表這張
 * 套票還沒設定組成——那時它不可售，後端下單也會擋。
 */
export function listProductComponents(productId: string): Promise<PackageComponent[]> {
  return apiFetch<PackageComponent[]>(`/products/${encodeURIComponent(productId)}/components`, {
    method: 'GET',
  });
}

/** F-3 景點介紹（GET /spots 為公開端點；KIOSK 前端過濾 is_published 取已發布）。 */
export interface SpotSummary {
  id: string;
  spot_code: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string> | null;
  latitude: string;
  longitude: string;
  trigger_radius_m: number;
  is_published: boolean;
}
export function listSpots(): Promise<SpotSummary[]> {
  return apiFetch<SpotSummary[]>('/spots', { method: 'GET' });
}

export function getRecommendations(payload: RecommendRequest): Promise<RecommendResponse> {
  return apiFetch<RecommendResponse>('/recommendations', { method: 'POST', body: payload });
}

/**
 * POST /pricing/quote — read-only 折後 unit_price 預覽。
 *
 * KIOSK 在 Schedule 選完班次 + 人數後呼叫；後續 Delivery / Invoice / Payment
 * 螢幕統一用回傳之 `total` 顯示，避免「點選 1500、收據 1200」之 UX 不一致。
 * 同一個 PricingResolver 與 OrdersService.create 共用，最終落庫值一致。
 */
export interface PricingQuoteTicket {
  ticket_type: KioskTicketType;
  quantity: number;
}
export interface PricingQuoteRequest {
  product_id: string;
  schedule_id: string;
  channel: 'web' | 'kiosk' | 'partner' | 'floor' | 'recovery';
  /** 各票種與數量；成人/兒童各自一行，後端分票種解析。 */
  tickets: PricingQuoteTicket[];
}
export interface PricingQuoteLine {
  ticket_type: string;
  quantity: number;
  unit_price: string;
  /** 折扣前原價（劃線價）；> unit_price 表示有促銷。 */
  original_price: string;
  line_total: string;
  pricing_id: string | null;
  rule_summary: string | null;
}
export interface PricingQuoteResponse {
  lines: PricingQuoteLine[];
  party_size: number;
  total: string;
}
export function getPricingQuote(payload: PricingQuoteRequest): Promise<PricingQuoteResponse> {
  return apiFetch<PricingQuoteResponse>('/pricing/quote', { method: 'POST', body: payload });
}

/** 可售票種 + 各票種基本單價（GET /pricing/ticket-types）。 */
export interface SellableTicketType {
  ticket_type: string;
  display_name_i18n: Record<string, string> | null;
  unit_price: string;
  /** 折扣前原價（劃線價）；> unit_price 表示有促銷。 */
  original_price: string;
  pricing_id: string | null;
  rule_summary: string | null;
}
/**
 * 可售票種 + 各票種單價。
 *
 * 套票不建班次，故 `schedule_id` 可省略——省略時後端只回商品價目表，不套 pricing
 * 規則（規則掛在 (product, schedule) 上，對套票沒有可匹配的對象）。
 */
export function getTicketTypes(params: {
  product_id: string;
  schedule_id?: string;
  channel: string;
}): Promise<{ ticket_types: SellableTicketType[] }> {
  const qs = new URLSearchParams({
    product_id: params.product_id,
    channel: params.channel,
    ...(params.schedule_id === undefined ? {} : { schedule_id: params.schedule_id }),
  });
  return apiFetch<{ ticket_types: SellableTicketType[] }>(
    `/pricing/ticket-types?${qs.toString()}`,
    {
      method: 'GET',
    },
  );
}

export function createOrder(
  payload: CreateOrderRequest,
  idempotencyKey: string,
): Promise<OrderSummary> {
  return apiFetch<OrderSummary>('/orders', {
    method: 'POST',
    body: payload,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** HOLD → PAYMENT_PENDING。 */
export function initiatePayment(orderId: string): Promise<{ order: OrderSummary }> {
  return apiFetch<{ order: OrderSummary }>(`/orders/${encodeURIComponent(orderId)}/events`, {
    method: 'POST',
    body: { event: 'initiate_payment' },
  });
}

/** PAYMENT_PENDING → PAID（同 tx 發 ticket）。 */
export function paymentCallback(
  orderId: string,
  providerTransactionId: string,
): Promise<PaymentCallbackResponse> {
  return apiFetch<PaymentCallbackResponse>(
    `/orders/${encodeURIComponent(orderId)}/payment-callback`,
    {
      method: 'POST',
      body: { status: 'success', provider_transaction_id: providerTransactionId },
    },
  );
}

// ---------- 服務帳號（僅票券 QR 用）----------

let serviceTokenPromise: Promise<string | null> | null = null;

async function loginService(): Promise<string | null> {
  try {
    const res = await apiFetch<{ access_token?: string; two_factor_required: boolean }>(
      '/auth/login',
      { method: 'POST', body: { email: KIOSK_EMAIL, password: KIOSK_PASSWORD } },
    );
    return res.access_token ?? null;
  } catch {
    // 服務帳號登入失敗不阻斷購票流程；出票畫面改走無 QR 顯示的降級。
    return null;
  }
}

function ensureServiceToken(): Promise<string | null> {
  if (serviceTokenPromise === null) serviceTokenPromise = loginService();
  return serviceTokenPromise;
}

/**
 * 拉票券 QR（出票後列印 / 螢幕顯示用）。服務帳號無權或登入失敗時回 null。
 */
export async function fetchTicketQr(ticketId: string): Promise<TicketQr | null> {
  const token = await ensureServiceToken();
  if (token === null) return null;
  try {
    return await apiFetch<TicketQr>(`/tickets/${encodeURIComponent(ticketId)}/qr`, {
      method: 'GET',
      token,
    });
  } catch {
    return null;
  }
}
